'use client';

import { PoweredByFooter } from '@/components/molecules/PoweredByFooter';
import dynamic from 'next/dynamic';
import ChargingIframe from '@/components/template/ChargingIframe/ChargingIframe';
import { useZipContext } from '@/context/ZipContext';
import { parseChargingInfo } from '@/lib/schema/inventory/parse';
import { RawChargingInfo, RawVehicleType } from '@/lib/schema/inventory/types';
import { Range } from '@/lib/schema/shared/types';
import { FuelTypes } from '@/types/enums';
import { DEFAULT_US_ZIP_CODE } from '@/lib/constants';
import { useEffect, useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { parseVehicleTrim } from '@/lib/parseVehicleTrim';
import { RawTrimSpecs } from '@/lib/schema/build/types';
import { parseRawMileage } from '@/lib/parseVehicle';

// Direct type definition to avoid import issues
type VehicleSpecsResponse = {
  Year: number;
  Make: string;
  Model: string;
  Trim: string;
  Battery?: {
    size?: string;
    [key: string]: unknown;
  } | null;
  Charging?: {
    [key: string]: unknown;
  } | null;
  RangeAllElectric?: {
    value?: number;
    unit?: string;
    [key: string]: unknown;
  } | null;
  RangeHybridElectric?: {
    value?: number;
    unit?: string;
    [key: string]: unknown;
  } | null;
  FuelType: string;
  Mileage?: {
    [key: string]: unknown;
  } | null;
};

type Props = {
  params: Promise<{ vin: string }>;
  searchParams: Promise<{ [key: string]: string | string[] | undefined }>;
};

function PageContent(props: Props) {
  const [selectedZipCode, setSelectedZipCode] = useState(DEFAULT_US_ZIP_CODE);
  const [vin, setVin] = useState<string | null>(null);
  const [showMap, setShowMap] = useState(false);
  // Always call useZipContext - this prevents hydration mismatches
  const zipContext = useZipContext();

  // Resolve params once and store the VIN
  useEffect(() => {
    const getVin = async () => {
      const params = await props.params;
      const searchParams = await props.searchParams;
      setVin(params.vin.toUpperCase());
      setShowMap(searchParams.showMap === 'true');
    };
    getVin();
  }, [props.params, props.searchParams]); // Added props.params to dependency array

  // Update selected zip code when context is available
  useEffect(() => {
    if (zipContext.preferredZip) {
      setSelectedZipCode(zipContext.preferredZip);
    }
  }, [zipContext.preferredZip]);

  // Fetch vehicle specs
  const { data: specs, isLoading: specsLoading } = useQuery({
    queryKey: ['vehicle-specs', vin],
    queryFn: async () => {
      if (!vin) return null;

      const response = await fetch(
        `/api/vehicle_details_page/vehicle_specs/${vin}`
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `HTTP error! status: ${response.status} - ${errorText}`
        );
      }

      return response.json() as Promise<VehicleSpecsResponse>;
    },
    enabled: !!vin && showMap,
    retry: false,
  });

  // Create range info from specs
  const rangeInfo = useMemo(() => {
    if (!specs) return null;

    const rangeValue =
      (specs.RangeAllElectric?.value as number) ??
      (specs.RangeHybridElectric?.value as number) ??
      0;
    const rangeUnit =
      (specs.RangeAllElectric?.unit as string) ??
      (specs.RangeHybridElectric?.unit as string) ??
      'mi';

    if (rangeValue > 0) {
      return {
        value: rangeValue,
        unit: rangeUnit,
      } as Range;
    }

    return null;
  }, [specs]);

  // Fetch vehicle listing for dealer information
  const { data: vehicleListing, isLoading: listingLoading } =
    useQuery<RawVehicleType | null>({
      queryKey: ['vehicle-listing', vin],
      queryFn: async () => {
        if (!vin) return null;

        const listingResponse = await fetch(
          `/api/inventory/listings/?vector_search=${vin}&limit=1`
        );

        if (!listingResponse.ok) {
          return null;
        }

        const listingData = await listingResponse.json();
        return listingData.content?.[0] || null;
      },
      enabled: !!vin && showMap,
      retry: false,
    });

  // Fetch charging info
  const { data: chargingInfoData, isLoading: chargingInfoLoading } = useQuery({
    queryKey: ['charging-info', vin],
    queryFn: async () => {
      if (!vin) {
        return null;
      }
      const response = await fetch(`/api/inventory/listing/${vin}`);
      if (!response.ok) {
        throw new Error(
          `Failed to fetch charging info: ${response.statusText}`
        );
      }
      const data = await response.json();
      return data as RawVehicleType;
    },
    enabled: !!vin,
    retry: false,
  });

  // Fetch trim specs
  const { data: trimSpecs, isLoading: trimSpecsLoading } = useQuery({
    queryKey: ['trim-specs', chargingInfoData?.StyleId],
    queryFn: async () => {
      if (!chargingInfoData?.StyleId) return null;

      const response = await fetch(
        `/api/compare/getVehicleStylesDataByStyleId/${chargingInfoData.StyleId}`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch trim specs: ${response.statusText}`);
      }

      const rawTrimSpecs: RawTrimSpecs = await response.json();
      return parseVehicleTrim(rawTrimSpecs);
    },
    enabled: !!chargingInfoData && !!chargingInfoData?.StyleId,
    retry: false,
  });

  const loading =
    specsLoading || listingLoading || chargingInfoLoading || trimSpecsLoading;

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <div>Loading vehicle data...</div>
          <div className="text-Body2Light mt-s">Please wait</div>
        </div>
      </div>
    );
  }

  // Create product object for ChargingIframe
  const product = chargingInfoData
    ? {
        batteryInfo: {
          batterySize: chargingInfoData?.Battery?.size || 'Coming soon',
          batteryTech: chargingInfoData?.Battery?.tech || 'Coming soon',
          batteryWarranty: chargingInfoData.Battery?.warranty || null,
        },
        batteryWarranty: chargingInfoData?.BatteryWarranty || '',
        chargingInfo: parseChargingInfo(
          chargingInfoData.Charging as RawChargingInfo,
          chargingInfoData.FuelType
        ),
        fuelType: chargingInfoData.FuelType as FuelTypes,
        rangeInfo: {
          value:
            chargingInfoData.RangeAllElectric?.value ??
            chargingInfoData.RangeHybridElectric?.value ??
            0,
          unit:
            chargingInfoData.RangeAllElectric?.unit ??
            chargingInfoData.RangeHybridElectric?.unit ??
            'm',
        } as Range,
        year: chargingInfoData.Year,
        make: chargingInfoData.Make,
        model: chargingInfoData.Model,
        trim: chargingInfoData.Trim,
        trimSpecs: trimSpecs || null,
        mileage: chargingInfoData.listing
          ? parseRawMileage(chargingInfoData.listing)
          : { value: 0, unit: 'mi' },
      }
    : null;

  return (
    <div className="min-h-screen bg-white">
      <ChargingIframe
        product={product}
        showFooter={false}
        rangeInfo={rangeInfo}
        vehicleListing={vehicleListing as RawVehicleType}
        selectedZipCode={selectedZipCode}
      />

      <div className="mt-l">
        <PoweredByFooter />
      </div>
    </div>
  );
}

// Wrap the entire page in a no-SSR boundary to prevent hydration issues
const Page = dynamic(() => Promise.resolve(PageContent), {
  ssr: false,
  loading: () => (
    <div className="flex h-screen items-center justify-center">
      <div className="text-center">
        <div>Loading...</div>
        <div className="text-Body2Light mt-[8px]">Initializing application</div>
      </div>
    </div>
  ),
});

export default Page;
